import { Box, Card, CardContent, FormControlLabel, Switch, Typography } from "@mui/material";
import i18n from "i18next";
import { faFileImport } from "@fortawesome/pro-regular-svg-icons";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { HorizontalBox } from "src/components/common/box/HorizontalBox.tsx";
import { VerticalBox } from "src/components/common/box/VerticalBox.tsx";
import { useStoredState } from "src/utils/useStoredState.ts";
import { SubConfigurationBulkSelectionView } from "src/components/views/erp/configurator/configuratorForm/configuratorTree/SubConfigurationBulkSelectionView.tsx";
import { PartConfigurationSchemaProperty } from "src/components/views/erp/configurator/configuratorForm/propertyModel/partConfigurationSdk.ts";
import { getAddablePropertiesForBulkSelection } from "src/components/views/erp/configurator/configuratorForm/propertyModel/propertyModelInputTree.ts";

export interface AddPropertyModelSubConfigurationViewProps {
	/** The sub-configuration properties to which a child can be added. */
	properties: PartConfigurationSchemaProperty[];
	addChildren: (properties: PartConfigurationSchemaProperty[]) => Promise<unknown>;
	closeDialog: () => Promise<unknown>;
	/** Imports sub-configurations from elsewhere instead, such as another line of the offer. */
	importAction?: { title: string; onClick: () => Promise<unknown> };
}

/**
 * Chooses the sub-configurations to add to a configuration of a property-model tree, one or many at once, as the
 * component form does.
 */
export function AddPropertyModelSubConfigurationView({
	properties,
	addChildren,
	closeDialog,
	importAction,
}: AddPropertyModelSubConfigurationViewProps) {
	// The component form's choice, so that both forms open the same way.
	const [useBulkInterface, setUseBulkInterface] = useStoredState<boolean>({
		type: "boolean",
		key: "A7F3B2E8D9C1F456",
		defaultValue: false,
	});

	return (
		<VerticalBox sx={{ flex: 1, margin: 2 }}>
			<HorizontalBox sx={{ justifyContent: "space-between" }}>
				{importAction && (
					<AsyncButton icon={faFileImport} label={importAction.title} onClick={importAction.onClick} />
				)}
				<FormControlLabel
					sx={{ marginLeft: "auto" }}
					control={
						<Switch
							checked={useBulkInterface}
							size={"small"}
							onChange={(_, checked) => setUseBulkInterface(checked)}
						/>
					}
					label={<Typography variant="body2">{i18n.t("multi_select")}</Typography>}
				/>
			</HorizontalBox>
			{useBulkInterface ?
				<SubConfigurationBulkSelectionView<PartConfigurationSchemaProperty>
					getAvailableItems={(selected) => getAddablePropertiesForBulkSelection(properties, selected)}
					getItemKey={(property) => property.name}
					getItemLabel={getPropertyLabel}
					closeDialog={closeDialog}
					onSubmit={addChildren}
				/>
			:	<SingleSelection properties={properties} addChildren={addChildren} />}
		</VerticalBox>
	);
}

function SingleSelection({
	properties,
	addChildren,
}: Pick<AddPropertyModelSubConfigurationViewProps, "properties" | "addChildren">) {
	return (
		<Box
			sx={{
				flex: 1,
				display: "grid",
				gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
				alignItems: "stretch",
				gap: 2,
				marginTop: 2,
			}}
		>
			{properties.map((property) => (
				<Card
					key={property.name}
					sx={{
						border: "1px solid",
						borderColor: "grey.300",
						borderRadius: 2,
						"&:hover": {
							transform: "translateY(-2px)",
							boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
							borderColor: "primary.main",
						},
					}}
				>
					<AsyncButton
						variant={"text"}
						fullWidth
						onClick={() => addChildren([property])}
						sx={{ height: "100%", minHeight: 80, borderRadius: 2, textTransform: "none" }}
					>
						<CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
							<Typography
								variant={"body1"}
								sx={{ textAlign: "center", fontWeight: 500, color: "text.primary", lineHeight: 1.3 }}
							>
								{getPropertyLabel(property)}
							</Typography>
						</CardContent>
					</AsyncButton>
				</Card>
			))}
		</Box>
	);
}

function getPropertyLabel(property: PartConfigurationSchemaProperty): string {
	return property.title || property.name;
}
